import { CLASSIC_COLORS, DOC_LABELS, MEMO_FONT_STACK, SHOP_COLORS, paperSize } from './invoiceEngine';
import ClassicMemo from './ClassicMemo';
import ShopMemo from './ShopMemo';
import PlainMemo from './PlainMemo';
import BorderMemo from './BorderMemo';
import SplitMemo from './SplitMemo';
import OutlineMemo from './OutlineMemo';

/** A4 at 96 dpi — what the preview falls back to before a paper is chosen. */
export const SHEET_WIDTH = 794;
export const SHEET_MIN_HEIGHT = 1123;

const INK = '#1C1815';

/**
 * Each pad is a printed design of its own and draws its whole sheet, so this
 * file only lays the paper, the mark under it, and hands over to the pad.
 */
const SHEETS = {
    memoClassic: ClassicMemo,
    memoBadge: BorderMemo,
    memoSplit: SplitMemo,
    memoOutline: OutlineMemo,
    memoShop: ShopMemo,
    memoRuled: PlainMemo,
};

/** The stock each pad is printed on. */
const STOCK = {
    memoClassic: CLASSIC_COLORS.paper,
    memoSplit: '#EAEAEA',
    memoShop: `linear-gradient(180deg, ${SHOP_COLORS.paperTop} 0%, ${SHOP_COLORS.paperMid} 42%, ${SHOP_COLORS.paperFoot} 100%)`,
};

/**
 * The faint mark across the middle of the pad — the shop's name set large, or
 * its logo. It is laid under everything and takes no clicks, so it never gets
 * in the way of what is written on the memo.
 */
function Watermark({ watermark, shop, accent, paperWidth }) {
    const { mode } = watermark;
    const label = watermark.text.trim() || shop.name.trim();
    // The picture is the one uploaded for the watermark, or the shop's logo when
    // none was uploaded — most shops want their logo and nothing else.
    const picture = watermark.image || shop.logo;
    if (mode === 'none') return null;
    if (mode === 'image' && !picture) return null;
    if (mode === 'text' && !label) return null;

    const width = (watermark.size / 100) * paperWidth;
    const frame = {
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        opacity: watermark.opacity / 100,
    };
    // Where its middle sits on the sheet: the shopkeeper slides it off centre
    // to keep it clear of the writing area.
    const placed = {
        position: 'absolute',
        left: `${watermark.x}%`,
        top: `${watermark.y}%`,
        transform: `translate(-50%, -50%) rotate(${watermark.angle}deg)`,
    };

    return (
        <div style={frame} aria-hidden="true">
            <div style={placed}>
                {mode === 'image' ? (
                    <img src={picture} alt="" style={{ width, maxWidth: 'none', display: 'block' }} />
                ) : (
                    <div
                        style={{
                            // Sized off the paper, not the viewport, so it keeps its
                            // proportions on an A6 pad and in the export alike.
                            fontSize: width / Math.max(4, label.length) * 1.6,
                            fontWeight: 800,
                            lineHeight: 1.1,
                            whiteSpace: 'nowrap',
                            color: accent,
                        }}
                    >
                        {label}
                    </div>
                )}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ *
 *  Sheet
 * ------------------------------------------------------------------ */

/**
 * One blank memo, drawn at the chosen paper size. `data-invoice-sheet` is what
 * the export captures.
 *
 * Everything inside is sized off `u`, the paper's width against A4, so an A6
 * pad is the same design printed small rather than A4 furniture crammed into a
 * quarter sheet.
 */
export default function InvoiceDocument({ invoice: memo }) {
    const lang = memo.docLang === 'en' ? 'en' : 'bn';
    const labels = DOC_LABELS[lang];
    const paper = paperSize(memo.paper);
    const u = paper.width / SHEET_WIDTH;
    const { accent } = memo;
    const title = memo.title.trim() || labels.title;
    const Sheet = SHEETS[memo.template] || SHEETS.memoClassic;

    return (
        <div
            data-invoice-sheet
            style={{
                width: paper.width,
                minHeight: paper.height,
                background: STOCK[memo.template] || '#fff',
                color: INK,
                fontFamily: MEMO_FONT_STACK,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}
        >
            <Watermark watermark={memo.watermark} shop={memo.shop} accent={accent} paperWidth={paper.width} />

            {/* The pad sits above the mark; without a stacking context of its
                own the pad's own backgrounds would cover it. */}
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Sheet
                    memo={memo}
                    labels={labels}
                    title={title}
                    accent={accent}
                    paperWidth={paper.width}
                    u={u}
                />
            </div>
        </div>
    );
}
